import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

/**
 * Capability tokens that the backend recognises via the `X-Rovo-Chat-Capabilities`
 * header. Each capability opts the request into a piece of backend behaviour.
 */
export const STORE_MESSAGE_FROM_USER_SETTINGS_CAPABILITY = 'store-message-from-user-settings';
export const ACTIONABLE_FOLLOW_UPS_CAPABILITY = 'actionable-follow-ups';

/**
 * Maps each gated capability to the gate/experiment that enables it. Add new
 * capability/gate pairs here rather than introducing nested ternaries at the
 * call sites.
 */
const GATED_CAPABILITIES: ReadonlyArray<{ capability: string; isEnabled: () => boolean }> = [
	{
		capability: STORE_MESSAGE_FROM_USER_SETTINGS_CAPABILITY,
		isEnabled: () => fg('ai_mate_store_messages_be_driven'),
	},
	{
		capability: ACTIONABLE_FOLLOW_UPS_CAPABILITY,
		// Do not fire an exposure here: enrolment is decided when the user actually interacts with
		// a rendered follow-up, not when we decide whether to request them.
		isEnabled: () =>
			FeatureGates.getExperimentValue('rovo_chat_actionable_follow_ups_exp', 'isEnabled', false, {
				fireExperimentExposure: false,
			}),
	},
];

/**
 * Builds the effective list of capabilities to send with a chat message by
 * appending any gate-enabled capabilities to the caller-provided list.
 *
 * Behaviour is preserved from the previous inline logic:
 * - When no gated capabilities are enabled, the original `capabilities` value is
 *   returned untouched (including `undefined`), so the header is only sent when
 *   there is something to send.
 * - The result is de-duplicated to avoid sending the same capability twice.
 */
export const buildEffectiveCapabilities = (capabilities?: string[]): string[] | undefined => {
	const enabledCapabilities = GATED_CAPABILITIES.filter(({ isEnabled }) => isEnabled()).map(
		({ capability }) => capability,
	);

	if (enabledCapabilities.length === 0) {
		return capabilities;
	}

	return Array.from(new Set([...(capabilities ?? []), ...enabledCapabilities]));
};
